import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import _ from 'underscore';
import { GlobalMenu } from '@bingads-webui-react/new-global-menu';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext, $current } from '@bingads-webui-react/scope-service';
import { SettingsContext } from '@bingads-webui-react/settings-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { convertFilteredCampaignTypeToFilter } from '@bingads-webui-campaign-react/shell-ui-common';
import { onToggleCopilot, toggleCopilotWithMSASignInPopup } from '@bingads-webui-react/msa-copilot-common';
import { GlobalFilterContext } from '@bingads-webui-react/global-filter-context';
import { WaterfallFilterType } from '@bingads-webui/filter-commons';
import { LayoutContext } from '@bingads-webui-react/app-layout-container';
import { renderPageSearch } from '@bingads-webui-react/global-search';
import { renderHelpLibrarySearch } from '@bingads-webui-react/global-search-help-library';
import { renderCampaignSearch } from '@bingads-webui-campaign-react/campaign-entity-search';
import { renderCopilotSearchWithCopilot } from '@bingads-webui/global-search-copilot';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import {
  withSiteMap,
  processorSet,
  processor,
  CampaignType,
} from '@bingads-webui-campaign-react/with-site-map';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { getHeaderHide, isBillingLinkPermission } from '@bingads-webui/multi-agency-utils';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';

import { DelayedNotificationsBar } from './delayed-notifications-bar';

export const NewGlobalMenuWrapper = (props) => {
  const serverContext = useContext(ServerContext);
  const settingsContext = useContext(SettingsContext);
  const instrumentation = useContext(InstrumentationContext);
  const permissions = useContext(PermissionsContext);
  const { scope } = useContext(ScopeContext);
  const { updateWaterfallFilter } = useContext(GlobalFilterContext);
  const { change: changeLayout, config: layoutConfig } = useContext(LayoutContext);
  const perfMarker = useMemo(
    () => props.appPerfMarker.createChild('new-global-menu-perf', { autoEnd: true }),
    [props.appPerfMarker]
  );
  const GlobalMenuEmbedded = withSiteMap(GlobalMenu, [processor.processPath, processor.processCopilotRegionRestriction, ...processorSet.basic]);

  useEffect(() => {
    perfMarker.willInit();
  }, [perfMarker]);

  const {
    CurrentAccount,
    Urls,
    CurrentUser,
    Omniture,
    PageTrackingId,
    IsProdEnv,
  } = serverContext;
  const { Email } = CurrentUser;
  const { SessionId } = Omniture;
  const supportUrl = Urls.RootUIUrl.replace(/^https:\/\//, 'https://internal.');
  const {
    Id, Name, AccountNumber, AdvertiserCustomerID,
  } = CurrentAccount;
  const account = {
    Id,
    Name,
    AccountNumber,
    ParentCustomerId: AdvertiserCustomerID,
  };
  const feedbackScenario = instrumentation.feedbackScenario.create();
  const logger = feedbackScenario.feedbackActivity.create();
  const feedbackLogger = {
    log: (info) => {
      logger.trace(info);
    },
  };
  const adsCopilotScenario = instrumentation.createScenario({
    scenarioName: 'AdsCopilot',
  });
  const adsCopilotLogger = adsCopilotScenario.createActivity('UserAction');

  const onLogoClick = useCallback(() => {
    if (_.isFunction(updateWaterfallFilter)) {
      updateWaterfallFilter({
        filterType: WaterfallFilterType.CampaignType,
        filter: convertFilteredCampaignTypeToFilter(CampaignType.All),
      });
    }

    scope.set({
      cid: $current,
      aid: $current,
      uid: $current,
    });

    // Don't specify a page, this should make page menu use the default landing page
    scope.save('/campaign/vnext');
  }, [scope, updateWaterfallFilter]);

  const onCopilotClick = useCallback(({ customizedEvent, forceOpen } = {}) => {
    const username = _.get(serverContext, ['CurrentUser', 'Name']);
    const localeId = _.get(serverContext, ['CurrentUser', 'LocaleID']);
    const isAADUser = _.get(serverContext, ['IsAADUser']);
    const isAppBasedAuthEnabled = _.get(permissions, ['dynamic', 'AdsCopilotAppBasedAuth']);
    const isNewLogo = _.get(permissions, ['dynamic', 'AdsCopilotUxImprovements']);

    const toggleCopilot = toggleCopilotWithMSASignInPopup({
      userName: username,
      localeId,
      isAADUser,
      isAppBasedAuthEnabled,
      toggleCopilot: (mode) => {
        adsCopilotLogger.trace(`[Copilot] Toggle copilot via Copilot button on header, mode: ${mode} isNewLogo: ${isNewLogo}`);
        onToggleCopilot({
          changeLayout,
          mode,
          logger: adsCopilotLogger,
          customizedEvent,
        });
      },
      logger: adsCopilotLogger,
    });
    const copilotMode = _.chain(layoutConfig).result('copilot').result('mode', 'hidden').value();
    const nextMode = (forceOpen || copilotMode === 'collapsed') ? 'expanded' : 'collapsed';
    toggleCopilot(nextMode);
  }, [serverContext, layoutConfig, adsCopilotLogger, changeLayout, permissions]);


  const campaignSearchEnabled = permissions && permissions.dynamic && permissions.dynamic.CampaignSearch;

  const [isAdsCopilotRegionRestricted, setIsAdsCopilotRegionRestricted] = useState();
  useEffect(() => {
    isCopilotRegionRestricted({
      feature: 'Chat',
      permissions,
      serverContext,
    }).then(setIsAdsCopilotRegionRestricted);
  }, [permissions, serverContext]);

  const accountIdEnabled = scope.values && scope.values.aid;
  const isCampaignPageAccessible = _.get(permissions, 'IsInternalUser') || !isBillingLinkPermission(settingsContext);
  const renderSearchResults = [];
  if (!isAdsCopilotRegionRestricted) {
    const isNewLogo = _.get(permissions, ['dynamic', 'AdsCopilotUxImprovements']);
    renderSearchResults.push(renderCopilotSearchWithCopilot(onCopilotClick, isNewLogo));
  }
  renderSearchResults.push(renderPageSearch);
  renderSearchResults.push(renderHelpLibrarySearch);
  if (campaignSearchEnabled && accountIdEnabled && isCampaignPageAccessible) {
    renderSearchResults.push(renderCampaignSearch);
  }

  const feedbackProps = {
    isTestEnv: !IsProdEnv,
    feedbackLogger,
    email: Email,
    pageId: PageTrackingId,
    sessionId: SessionId,
    hideFeedback: permissions.IsReadOnlyUser,
  };

  const hide = {
    copilot: isAdsCopilotRegionRestricted,
    ...(!_.get(permissions, 'IsInternalUser') && getHeaderHide(settingsContext)),
  };

  setClarityTag({ NavigationRefresh: 'true' });

  return (
    <GlobalMenuEmbedded
      account={account}
      feedbackProps={feedbackProps}
      serverContext={serverContext}
      settingsContext={settingsContext}
      perfMarker={perfMarker}
      supportUrl={supportUrl}
      NotificationsBar={DelayedNotificationsBar}
      onLogoClick={onLogoClick}
      onCopilotClick={onCopilotClick}
      renderSearchResults={renderSearchResults}
      hide={hide}
    />
  );
};

NewGlobalMenuWrapper.displayName = 'NewGlobalMenuWrapper';
NewGlobalMenuWrapper.propTypes = {
  appPerfMarker: perfMarkerPropTypes.isRequired,
};
